import { useCallback, useEffect, useMemo, useState } from 'react';

import { Icon } from 'components/icons/Icon';
import { Card } from 'components/ui/card';
import { useEventTracking } from 'hooks/useEventTracking';
import { cn } from 'lib/utils';
import { dailyPulse, type DailyPulseOverview, type DailyPulseStatus } from 'services/api';
import {
  requestDailyCheckIn,
  requestDailyPulseCustomization,
  subscribeToDailyCheckInUpdates,
  type DailyCheckInRequest,
} from 'utils/dailyCheckIn';

import './DailyCheckIn.css';

interface DailyCheckInSectionProps {
  summary?: DailyPulseOverview | null;
  unavailable?: boolean;
}

type CardTone = 'warm' | 'calm' | 'muted';

interface CardCopy {
  tone: CardTone;
  pill: string;
  title: string;
  subcopy: string;
  cta: string;
  action: () => void;
}

const dayStatusCopy: Record<DailyPulseStatus, string> = {
  due: 'Daily Pulse due. Open to fill it in.',
  saved: 'Daily Pulse saved. Open to edit.',
  skipped: 'Daily Pulse skipped. Open to fill it in.',
  missed: 'Daily Pulse not filled in. Open to add it.',
  disabled: 'Daily Pulse is turned off.',
};

const formatFullDate = (value: string) => {
  const [year, month, day] = value.split('-').map(Number);
  return new Intl.DateTimeFormat('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
  }).format(new Date(year, month - 1, day));
};

export const DailyCheckInSection = ({ summary, unavailable = false }: DailyCheckInSectionProps) => {
  const { track } = useEventTracking();
  const [current, setCurrent] = useState(summary || null);
  const [retrying, setRetrying] = useState(false);
  const [retryFailed, setRetryFailed] = useState(false);

  const retry = async () => {
    setRetrying(true);
    try {
      setCurrent(await dailyPulse.getOverview());
      setRetryFailed(false);
    } catch {
      setRetryFailed(true);
    } finally {
      setRetrying(false);
    }
  };

  useEffect(() => setCurrent(summary || null), [summary]);

  useEffect(() => subscribeToDailyCheckInUpdates(() => {
    dailyPulse.getOverview().then(setCurrent).catch(() => {
      // Home keeps its last known summary if the quiet refresh fails.
    });
  }), []);

  const openCheckIn = useCallback((request: DailyCheckInRequest = {}) => {
    track('button_click', { action: 'open_daily_check_in' });
    requestDailyCheckIn(request);
  }, [track]);

  const customize = () => {
    track('button_click', { action: 'customize_daily_pulse' });
    requestDailyPulseCustomization({ fromDailyPulse: false });
  };

  const copy = useMemo<CardCopy | null>(() => {
    if (!current) return null;
    const goals = current.goal_summary;
    const handledGoals = goals.updated + goals.skipped;
    const remaining = goals.due + (current.pulse_due ? 1 : 0);
    const goalNoun = goals.due === 1 ? 'goal' : 'goals';

    if (remaining > 0) {
      if (handledGoals > 0 && current.pulse_due && goals.due === 0) {
        return {
          tone: 'warm',
          pill: '1 item left',
          title: 'One step left',
          subcopy: "Daily Pulse is all that's left today.",
          cta: 'Continue check-in',
          action: () => openCheckIn(),
        };
      }
      if (!current.has_enabled_questions) {
        return {
          tone: 'warm',
          pill: `${goals.due} ${goalNoun} due`,
          title: 'Goals are ready',
          subcopy: 'Daily Pulse is turned off. Turn questions back on whenever you want it.',
          cta: 'Check in now',
          action: () => openCheckIn(),
        };
      }
      const waiting = [
        goals.due ? `${goals.due === 1 ? 'One' : goals.due} ${goalNoun}` : '',
        current.pulse_due ? 'Daily Pulse' : '',
      ].filter(Boolean).join(' and ');
      return {
        tone: 'warm',
        pill: `${remaining} ${remaining === 1 ? 'item' : 'items'} left`,
        title: "Today's check-in is ready",
        subcopy: `${waiting} ${waiting.includes(' and ') ? 'are' : 'is'} waiting — about a minute.`,
        cta: handledGoals ? 'Continue check-in' : 'Check in now',
        action: () => openCheckIn(),
      };
    }

    if (current.today_status === 'saved') {
      const goalCopy = goals.updated || goals.skipped
        ? [
          goals.updated ? `${goals.updated} ${goals.updated === 1 ? 'goal' : 'goals'} updated` : '',
          goals.skipped ? `${goals.skipped} ${goals.skipped === 1 ? 'goal' : 'goals'} skipped` : '',
        ].filter(Boolean).join(' · ')
        : '';
      return {
        tone: 'calm',
        pill: 'Complete',
        title: "You're caught up for today",
        subcopy: `${goalCopy}${goalCopy ? ' · ' : ''}Daily Pulse saved.`,
        cta: 'View today',
        action: () => openCheckIn({ targetDate: current.today, pulseOnly: true, forcePulse: true }),
      };
    }

    if (current.today_status === 'skipped') {
      return {
        tone: 'muted',
        pill: 'Pulse skipped',
        title: 'Daily Pulse skipped for today',
        subcopy: 'You can still fill it in before the day ends, or come back to it later this week.',
        cta: 'Complete anyway',
        action: () => openCheckIn({ targetDate: current.today, pulseOnly: true, forcePulse: true }),
      };
    }

    return {
      tone: 'muted',
      pill: 'Nothing due',
      title: 'Nothing scheduled for today',
      subcopy: 'Add a note whenever you want to remember the day.',
      cta: 'Add a daily note',
      action: () => openCheckIn({ targetDate: current.today, pulseOnly: true, forcePulse: true }),
    };
  }, [current, openCheckIn]);

  if (!current || !copy) {
    return (
      <Card variant="glass" maxWidth="none">
        <div className="daily-checkin-card daily-checkin-card--loading" aria-busy={retrying || (!unavailable && !retryFailed)}>
          {unavailable || retryFailed ? <div role="status"><p>Unable to load your Daily Check-in right now.</p><button type="button" className="daily-checkin-card__primary" onClick={() => void retry()} disabled={retrying}>{retrying ? 'Retrying…' : 'Retry Daily Check-in'}</button></div> : <span>Loading Daily Check-in…</span>}
        </div>
      </Card>
    );
  }

  return (
    <Card variant="glass" maxWidth="none" className="daily-checkin-card-shell">
      <section className="daily-checkin-card" aria-label="Daily Check-in">
        <div className="daily-checkin-card__copy">
          <div className="daily-checkin-card__topline">
            <span className="daily-checkin-card__eyebrow">Daily check-in</span>
            <span className={`daily-checkin-card__pill daily-checkin-card__pill--${copy.tone}`}>{copy.pill}</span>
          </div>
          <h3>{copy.title}</h3>
          <p>{copy.subcopy}</p>
          <div className="daily-checkin-card__actions">
            <button type="button" className="daily-checkin-card__primary" onClick={copy.action}>{copy.cta}</button>
            <button type="button" className="daily-checkin-card__customize" onClick={customize}>Customize questions</button>
          </div>
        </div>

        <div className="daily-checkin-card__history">
          <span className="daily-checkin-card__history-label">Recent Daily Pulse</span>
          <div className="daily-checkin-card__days" role="group" aria-label="Daily Pulse for the last seven days" data-testid="daily-pulse-days">
            {current.days.map((day) => {
              const disabled = day.status === 'disabled' && !day.is_today;
              return (
                <button
                  key={day.date}
                  type="button"
                  className={cn('daily-checkin-day', `daily-checkin-day--${day.status}`)}
                  aria-label={`${formatFullDate(day.date)}${day.is_today ? ' — today, ' : ' — '}${dayStatusCopy[day.status]}`}
                  aria-current={day.is_today ? 'date' : undefined}
                  disabled={disabled}
                  onClick={() => openCheckIn({
                    targetDate: day.date,
                    pulseOnly: !day.is_today,
                    forcePulse: true,
                  })}
                >
                  <span className="daily-checkin-day__weekday" aria-hidden="true">{day.weekday}</span>
                  {day.status === 'saved' ? (
                    <Icon name="orangeCheckmark" size={40} className="daily-checkin-day__check" aria-hidden="true" />
                  ) : (
                    <span className="daily-checkin-day__pip" aria-hidden="true">
                      {day.status === 'skipped' ? '–' : day.day_number}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      </section>
    </Card>
  );
};
